import { useCallback, useEffect } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { ClientIdType } from '~/enums/ClientIdType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withPermissions } from '~/hocs/withPermissions';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { checkIsSuperAdmin } from '~/permissions/checkIsSuperAdmin';
import { testHasClientId } from '~/permissions/utils/testHasClientId';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import { testIsSuperAdmin } from '~/utils/user';

type Props = {
  //router
  location: any;
  //injected
  checkPermission: (resourceId: string, resourceType: string) => boolean;
  isPermissionsLoading: boolean;
};

const PERMISSIONS = [
  {
    resourceId: 'users',
    resourceType: 'view',
  },
  {
    resourceId: 'roles',
    resourceType: 'view',
  },
  {
    resourceId: 'departments',
    resourceType: 'view',
  },
  {
    resourceId: 'email_intake',
    resourceType: 'view',
  },
  {
    resourceId: 'data_fields',
    resourceType: 'view',
  },
  {
    resourceId: 'integrations',
    resourceType: 'view',
  },
  {
    resourceId: 'sso',
    resourceType: 'view',
  },
  {
    resourceId: 'restoration_hub',
    resourceType: 'view',
  },
  //More settings permissions
  {
    resourceId: 'root_folder_access_settings',
    resourceType: 'view',
  },
  {
    resourceId: 'documents_metadata_bulk_upload',
    resourceType: 'view',
  },
  {
    resourceId: 'clause_quick_ai',
    resourceType: 'view',
  },
  {
    resourceId: 'deleted_documents',
    resourceType: 'view',
  },
  {
    resourceId: 'audit_logs',
    resourceType: 'view',
  },
];

const Page = ({ isPermissionsLoading, checkPermission }: Props) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { navigate, params } = useRouting();

  const { clientId } = params as { clientId: string };
  const isInboundEmailFeatureEnabled = useHasFeatureFlag(
    FeatureFlagType.InboundEmail,
  );
  const hasNewUsersPage = useFlag(FlagType.NewUsersPage);
  const isSuperAdmin = checkIsSuperAdmin(currentUser);
  const isGeneralClient =
    testHasClientId(ClientIdType.General)(currentUser) &&
    parseInt(clientId) === 1;

  const usersUrl = !hasNewUsersPage
    ? `/admin/console/${clientId || currentUser.client}/users`
    : `/admin/${clientId}/users`;

  const superAdminUsersUrl = !hasNewUsersPage
    ? `/admin/console/${clientId ?? currentUser.client}/users`
    : '/super-admin/users';

  const { data: isWorkdayUser = false, isLoading: isWorkdayUserLoading } =
    api.endpoints.getIsWorkdayUser.useQuery({}, { skip: isSuperAdmin });
  const { data: isWorkdayClient = false, isLoading: isWorkdayClientLoading } =
    api.endpoints.getIsWorkdayClient.useQuery(
      { clientId: Number(clientId) },
      { skip: !isSuperAdmin },
    );

  const isWorkday = isSuperAdmin ? isWorkdayClient : isWorkdayUser;

  //is this deprecated?
  const hasWorkflow = useHasFeatureFlag('WORKFLOW' as FeatureFlagType);

  const hasSingleSignOn = useHasFeatureFlag(FeatureFlagType.SingleSignOn);
  const hasSuperAdminSamlSSO = useFlag(FlagType.SuperAdminSamlSSO);
  const hasAdminSamlSSO = hasSingleSignOn && checkPermission('sso', 'view');
  const isSAMLSSOVisible =
    !isWorkday &&
    (testIsSuperAdmin(currentUser) ? hasSuperAdminSamlSSO : hasAdminSamlSSO);
  const hasCopyWorkflow = useFlag(FlagType.CopyWorkflow);
  const {
    usersDescription,
    departmentsDescription,
    rolesDescription,
    emailIntakeDescription,
    dataFieldsDescription,
    integrationsDescription,
    copyWorkflowDescription,
    restorationHubDescription,
    moreSettingsDescription,
    documentProcessingDescription,
    managePartyListsDescription,
    singleSignOnDescription,
    auditLogsDescription,
    manageClientsDescription,
    rolesTitle,
    usersTitle,
    departmentsTitle,
    emailIntakeTitle,
    dataFieldsTitle,
    integrationsTitle,
    copyWorkflowTitle,
    restorationHubTitle,
    moreSettingsTitle,
    documentProcessingTitle,
    managePartyListsTitle,
    singleSignOnTitle,
    auditLogsTitle,
    manageClientsTitle,
  } = getAdminConsoleTitlesAndDescriptions(intl);

  //Super Admin Guard
  useEffect(() => {
    if (!isSuperAdmin && !clientId) {
      navigate(`/admin/console/${currentUser.client}`);
    }
  }, [isSuperAdmin, clientId]);

  const canViewMoreSettings = useCallback(() => {
    return (
      checkPermission('root_folder_access_settings', 'view') ||
      checkPermission('documents_metadata_bulk_upload', 'view') ||
      checkPermission('clause_quick_ai', 'view') ||
      checkPermission('deleted_documents', 'view')
    );
  }, [checkPermission]);

  const workflowLinks = [
    {
      id: OnboardingIdType.AdminUserTab,
      title: usersTitle,
      description: usersDescription,
      url: usersUrl,
      visible: checkPermission('users', 'view'),
    },
    {
      title: rolesTitle,
      description: rolesDescription,
      url: `/admin/${clientId}/roles`,
      visible: hasNewUsersPage && checkPermission('roles', 'view'),
    },
  ];

  const superAdminLinks = [
    {
      id: OnboardingIdType.AdminUserTab,
      title: usersTitle,
      description: usersDescription,
      url: superAdminUsersUrl,
      visible: true,
    },
    {
      title: manageClientsTitle,
      description: manageClientsDescription,
      url: '/admin/',
      visible: true,
    },
    {
      id: OnboardingIdType.AdminDepartmentTab,
      title: departmentsTitle,
      description: departmentsDescription,
      url: `/admin/console/${clientId}/departments`,
      visible: true,
    },
    {
      title: emailIntakeTitle,
      description: emailIntakeDescription,
      url: `/admin/console/${clientId}/email-intake`,
      visible: true,
    },
    {
      title: dataFieldsTitle,
      description: dataFieldsDescription,
      url: `/admin/console/${clientId}/data-fields`,
      visible: true,
    },
    {
      title: integrationsTitle,
      description: integrationsDescription,
      url: `/admin/console/${clientId}/integrations`,
      visible: true,
    },
    {
      title: copyWorkflowTitle,
      description: copyWorkflowDescription,
      url: `/admin/console/${clientId}/copy-workflow`,
      visible: hasCopyWorkflow && checkIsSuperAdmin(currentUser),
    },
    {
      title: restorationHubTitle,
      description: restorationHubDescription,
      url: `/admin/console/${clientId}/restoration-hub`,
      visible: true,
    },
    {
      title: moreSettingsTitle,
      description: moreSettingsDescription,
      url: `/admin/console/${clientId}/more-settings`,
      visible: true,
    },
    {
      title: documentProcessingTitle,
      description: documentProcessingDescription,
      url: `/admin/console/${clientId}/process-documents`,
      visible: checkIsSuperAdmin(currentUser),
    },
    {
      title: managePartyListsTitle,
      description: managePartyListsDescription,
      url: `/admin/console/${clientId}/manage-party-lists`,
      visible: checkIsSuperAdmin(currentUser),
    },
  ];

  const adminLinks = [
    {
      id: OnboardingIdType.AdminUserTab,
      title: usersTitle,
      description: usersDescription,
      url: usersUrl,
      visible: checkPermission('users', 'view'),
    },
    {
      id: OnboardingIdType.AdminDepartmentTab,
      title: departmentsTitle,
      description: departmentsDescription,
      url: `/admin/console/${clientId}/departments`,
      visible: checkPermission('departments', 'view'),
    },
    {
      title: rolesTitle,
      description: rolesDescription,
      url: `/admin/${clientId}/roles/`,
      visible: hasNewUsersPage && checkPermission('roles', 'view'),
    },
    {
      title: documentProcessingTitle,
      description: documentProcessingDescription,
      url: `/admin/console/${clientId}/process-documents`,
      visible: checkIsSuperAdmin(currentUser),
    },
    {
      title: managePartyListsTitle,
      description: managePartyListsDescription,
      url: `/admin/console/${clientId}/manage-party-lists`,
      visible: checkIsSuperAdmin(currentUser),
    },
    {
      title: copyWorkflowTitle,
      description: copyWorkflowDescription,
      url: `/admin/console/${clientId}/copy-workflow`,
      visible: hasCopyWorkflow && checkIsSuperAdmin(currentUser),
    },
    {
      title: emailIntakeTitle,
      description: emailIntakeDescription,
      url: `/admin/console/${clientId}/email-intake`,
      visible:
        isInboundEmailFeatureEnabled && checkPermission('email_intake', 'view'),
    },
    {
      title: dataFieldsTitle,
      description: dataFieldsDescription,
      url: `/admin/console/${clientId}/data-fields`,
      visible: checkPermission('data_fields', 'view'),
    },
    {
      title: integrationsTitle,
      description: integrationsDescription,
      url: `/admin/console/${clientId}/integrations`,
      visible: checkPermission('integrations', 'view'),
    },
    {
      id: OnboardingIdType.AdminSamlTab,
      title: singleSignOnTitle,
      description: singleSignOnDescription,
      url: `/admin/console/${clientId}/saml-configuration`,
      visible: isSAMLSSOVisible,
    },
    {
      title: restorationHubTitle,
      description: restorationHubDescription,
      url: `/admin/console/${clientId}/restoration-hub`,
      visible: checkPermission('restoration_hub', 'view'),
    },
    {
      title: moreSettingsTitle,
      description: moreSettingsDescription,
      url: `/admin/console/${clientId}/more-settings`,
      visible: canViewMoreSettings(),
    },
    {
      title: auditLogsTitle,
      description: auditLogsDescription,
      url: `/admin/console/${clientId}/audit-logs`,
      visible: checkPermission('audit_logs', 'view'),
    },
  ];

  const getLinks = () => {
    let links;
    if (isSuperAdmin && isGeneralClient) {
      links = superAdminLinks;
    } else if (hasWorkflow) {
      links = workflowLinks;
    } else {
      links = adminLinks;
    }

    return links.filter((link) => link.visible);
  };

  const renderConsoleItems = () => {
    const links = getLinks();

    return links.map((link, index) => (
      <Layout
        role="menu"
        key={`admin_link_${index}`}
        pt={4}
        onClick={() => navigate(link.url)}
      >
        <Layout
          borderBottom={index === links.length - 1 ? undefined : 'border'}
          mr={4}
          ml={4}
          pb={4}
          w="100%"
          justify="space-between"
          align="center"
        >
          <Layout direction="column" id={link.id}>
            <Text variant="section" role="menuitem">
              {link.title}
            </Text>
            <Text variant="body" color="text.secondary" role="menuitem">
              {link.description}
            </Text>
          </Layout>
          <Icon icon="chevron-right"></Icon>
        </Layout>
      </Layout>
    ));
  };

  return (
    <PageLayout
      loadingContent={{
        isLoading:
          isPermissionsLoading ||
          isWorkdayUserLoading ||
          isWorkdayClientLoading,
      }}
      title={intl.formatMessage({
        id: 'AdminConsole.pageTitle',
        defaultMessage: 'Admin Console',
        description: 'Admin Console page title',
      })}
    >
      <Layout direction="column" border="border">
        {renderConsoleItems()}
      </Layout>
    </PageLayout>
  );
};

export default withPermissions(Page, PERMISSIONS);

const getAdminConsoleTitlesAndDescriptions = (intl: IntlShape) => {
  const rolesTitle = intl.formatMessage({
    id: 'AdminConsole.rolesTitle',
    defaultMessage: 'Roles',
    description: 'Roles section title',
  });

  const usersTitle = intl.formatMessage({
    id: 'AdminConsole.usersTitle',
    defaultMessage: 'Users',
    description: 'Users section title',
  });

  const departmentsTitle = intl.formatMessage({
    id: 'AdminConsole.departmentsTitle',
    defaultMessage: 'Departments',
    description: 'Departments section title',
  });

  const emailIntakeTitle = intl.formatMessage({
    id: 'AdminConsole.emailIntakeTitle',
    defaultMessage: 'Email Intake',
    description: 'Email Intake section title',
  });

  const dataFieldsTitle = intl.formatMessage({
    id: 'AdminConsole.dataFieldsTitle',
    defaultMessage: 'Data Fields',
    description: 'Data Fields section title',
  });

  const integrationsTitle = intl.formatMessage({
    id: 'AdminConsole.integrationsTitle',
    defaultMessage: 'Integrations',
    description: 'Integrations section title',
  });

  const copyWorkflowTitle = intl.formatMessage({
    id: 'AdminConsole.copyWorkflowTitle',
    defaultMessage: 'Copy Workflow',
    description: 'Copy Workflow section title',
  });

  const restorationHubTitle = intl.formatMessage({
    id: 'AdminConsole.restorationHubTitle',
    defaultMessage: 'Restoration Hub',
    description: 'Restoration Hub section title',
  });

  const moreSettingsTitle = intl.formatMessage({
    id: 'AdminConsole.moreSettingsTitle',
    defaultMessage: 'More Settings',
    description: 'More Settings section title',
  });

  const auditLogsTitle = intl.formatMessage({
    id: 'AdminConsole.auditLogsTitle',
    defaultMessage: 'Audit Logs',
    description: 'Audit Logs section title',
  });

  const documentProcessingTitle = intl.formatMessage({
    id: 'AdminConsole.documentProcessingTitle',
    defaultMessage: 'Document Processing',
    description: 'Document Processing section title',
  });

  const managePartyListsTitle = intl.formatMessage({
    id: 'AdminConsole.managePartyListsTitle',
    defaultMessage: 'Manage Party Lists',
    description: 'Manage Party Lists section title',
  });

  const manageClientsTitle = intl.formatMessage({
    id: 'AdminConsole.manageClientsTitle',
    defaultMessage: 'Manage Clients',
    description: 'Manage Clients section title',
  });

  const singleSignOnTitle = intl.formatMessage({
    id: 'AdminConsole.singleSignOnTitle',
    defaultMessage: 'Single Sign-On (SSO)',
    description: 'Single Sign-On section title',
  });

  // Consolidated description messages
  const usersDescription = intl.formatMessage({
    id: 'AdminConsole.usersDescription',
    defaultMessage:
      'Add, update, and remove users from your organization and manage access control settings.',
    description: 'Users section description',
  });

  const departmentsDescription = intl.formatMessage({
    id: 'AdminConsole.departmentsDescription',
    defaultMessage:
      'Set up departments and assign users to them to easily manage things like access control settings.',
    description: 'Departments section description',
  });

  const rolesDescription = intl.formatMessage({
    id: 'AdminConsole.rolesDescription',
    defaultMessage:
      ' Manage and create custom roles to control user permissions.',
    description: 'Roles section description',
  });

  const emailIntakeDescription = intl.formatMessage({
    id: 'AdminConsole.emailIntakeDescription',
    defaultMessage: 'Manage the different email intake rules.',
    description: 'Email Intake section description',
  });

  const dataFieldsDescription = intl.formatMessage({
    id: 'AdminConsole.dataFieldsDescription',
    defaultMessage:
      'Configure data fields for documents to suit your organization needs.',
    description: 'Data Fields section description',
  });

  const integrationsDescription = intl.formatMessage({
    id: 'AdminConsole.integrationsDescription',
    defaultMessage:
      'Connect Evisort with your external platforms and services.',
    description: 'Integrations section description',
  });

  const copyWorkflowDescription = intl.formatMessage({
    id: 'AdminConsole.copyWorkflowDescription',
    defaultMessage: 'Copy workflows from one client to another.',
    description: 'Copy Workflow section description',
  });

  const restorationHubDescription = intl.formatMessage({
    id: 'AdminConsole.restorationHubDescription',
    defaultMessage: 'You can restore any deleted items here.',
    description: 'Restoration Hub section description',
  });

  const moreSettingsDescription = intl.formatMessage({
    id: 'AdminConsole.moreSettingsDescription',
    defaultMessage: 'Configure more settings for your organization.',
    description: 'More Settings section description',
  });

  const documentProcessingDescription = intl.formatMessage({
    id: 'AdminConsole.documentProcessingDescription',
    defaultMessage:
      'View the number of documents processed and rerun failed algorithms.',
    description: 'Document Processing section description',
  });

  const managePartyListsDescription = intl.formatMessage({
    id: 'AdminConsole.managePartyListsDescription',
    defaultMessage: 'Edit lists of internal parties and counterparties.',
    description: 'Manage Party Lists section description',
  });

  const singleSignOnDescription = intl.formatMessage({
    id: 'AdminConsole.singleSignOnDescription',
    defaultMessage:
      'Configure and manage SAML-based Single Sign-On for your organization.',
    description: 'Single Sign-On section description',
  });

  const auditLogsDescription = intl.formatMessage({
    id: 'AdminConsole.auditLogsDescription',
    defaultMessage: 'View and export audit logs for your organization',
    description: 'Audit Logs section description',
  });

  const manageClientsDescription = intl.formatMessage({
    id: 'AdminConsole.manageClientsDescription',
    defaultMessage:
      'Access clients and mirror client users to carry out administrative tasks.',
    description: 'Manage Clients section description',
  });

  return {
    usersDescription,
    departmentsDescription,
    rolesDescription,
    emailIntakeDescription,
    dataFieldsDescription,
    integrationsDescription,
    copyWorkflowDescription,
    restorationHubDescription,
    moreSettingsDescription,
    documentProcessingDescription,
    managePartyListsDescription,
    singleSignOnDescription,
    auditLogsDescription,
    manageClientsDescription,
    rolesTitle,
    usersTitle,
    departmentsTitle,
    emailIntakeTitle,
    dataFieldsTitle,
    integrationsTitle,
    copyWorkflowTitle,
    restorationHubTitle,
    moreSettingsTitle,
    documentProcessingTitle,
    managePartyListsTitle,
    singleSignOnTitle,
    auditLogsTitle,
    manageClientsTitle,
  };
};
